import React, { useEffect, Suspense, lazy, useState } from "react";
import {
  Card,
  Col,
  Space,
  Button,
  Tooltip,
  Skeleton,
  Select,
  Typography,
  Row,
} from "antd";
import { CloseOutlined, EyeOutlined } from "@ant-design/icons";
// import ActionBtns from "/src/components/ActionBtns/ActionBtns";
import { Route, useRouteMatch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  activeAdjustedExperienceSelector,
  experienceLoadingSelector,
} from "../selector";
import { topicsSelector } from "../../Segments/Topics/selector";
import { appTypeSelector } from "/src/App/OfflineApp/offlineAppSelectors";
import { actions as experienceReduxActions } from "/src/views/Experiences/redux";
import { isEmpty, upperFirst } from "lodash";
import RenderHtml from "/src/components/UI/RenderHtml/RenderHtml";
import DuplicateExperienceButton from "../ExperienceShow/Components/DuplicateExperienceButton";
import "./ExperienceItemPreview.scss";
import { useTranslate } from "/src/lib/MrTranslate/MrTranslate";
import { showExperienceHelper, unMountExperience } from "../ExperienceHelperMethods";
import { currentUserSelector } from "../../Auth/Login/selector";
import { actions as firestoreInteractionActions } from "/src/views/Experiences/ExperienceShow/FirestoreInteractions/redux";
const LazySegments = lazy(() => import("../../Segments/Segments"));

const ExperienceItemPreview = (props) => {
  const { isLibrary, location, history, resourceName} = props;
  const { path } = useRouteMatch();
  const experience = useSelector(activeAdjustedExperienceSelector(isLibrary));
  const topics = useSelector(topicsSelector());
  const experienceLoading = useSelector(experienceLoadingSelector());
  const currentUser = useSelector(currentUserSelector());
  const appType = useSelector(appTypeSelector());
  const dispatch = useDispatch();
  const mrIntl = useTranslate();
  const { Text } = Typography;
  const defaultTopicId = topics && topics.length > 0 ? topics[0]?.id : null;
  const experienceName = experience.name;
  const [activeTopicId, setActiveTopicId] = useState(defaultTopicId);
  // Get the active topic from topics array
  const activeTopic = topics.find((topic) => topic.id === activeTopicId);
  const { Option } = Select;
  let topicOptions = [];

  // Retrieve the Topic titles and push them into topicOptions array to show in the Select dropdown component
  topics.map((topic, i) => {
    const topicTitle = topic.section_segment_attributes?.title;
    topicOptions.push(
      <Option
        key={`${topicTitle}-${topic.id}`}
        value={topic.id}
        className="topic-select-option"
      >
        <RenderHtml
          truncate={true}
          extraClassname="topic-select-option-text"
          text={topicTitle}
        />
      </Option>
    );
  });

  const resetScrollPosition = () => {
    const experiencePreviewContainer = document.querySelector(
      ".experience-item-preview-card"
    );
    if (experiencePreviewContainer) {
      experiencePreviewContainer.scrollTop = 0;
    }
  };

  // Reset active topic when experience changes
  useEffect(() => {
    setActiveTopicId(defaultTopicId);
    resetScrollPosition();
  }, [experience.id, defaultTopicId]);

  // Close preview when experience object is empty (occurs when changing pages, as the current experience is removed from Redux)
  useEffect(() => {
    if (isEmpty(experience)) {
      closePreview();
    }
  }, [experience]);

  // Closing preview on unmount
  useEffect(() => {
    return () => {
      dispatch(experienceReduxActions.setShowPreviewSuccess({ value: false }));
    };
  }, [dispatch]);

  const duplicateBtn = (
    <DuplicateExperienceButton
      item={experience}
      sourceId={experience.uid}
      isLibrary={true}
      withText={true}
      buttonShape="square"
    />
  );

  const closePreview = () => {
    unMountExperience(experience, appType, currentUser, dispatch);
  };

  const handleSelectChange = (topicId) => {
    setActiveTopicId(topicId);
    resetScrollPosition();
  };

  const setLogs = (action) => {
    let msg = `Assessment ${experience.name} (Join Code: ${experience.access_code}) was opened by ${currentUser.name} (${upperFirst(currentUser.role)})`

    dispatch(
      firestoreInteractionActions.setLogs({
        logging: true,
        log_type: "experience",
        category: "experience_crud",
        msg: msg,
        itemId: experience.id,
        experienceId: experience.id
      })
    );
  }

  return (
    <Card
      className="experience-item-preview-card"
      title={
        <Row className="experience-item-preview-card-header" gutter={16}>
          <Col span={8}>
            <Text className="card-header-title">{experienceName}</Text>
          </Col>
          <Col span={8} className="card-header-topic-select-container">
            {/* Ensure there is at least one topic available before rendering the Select component */}
            {defaultTopicId && (
              <Select
                key={`topic-select-${experience.id}`}
                onChange={handleSelectChange}
                className="card-header-topic-select"
                value={activeTopicId}
                getPopupContainer={() =>
                  document.querySelector(".experience-item-preview-card")
                }
              >
                {topicOptions}
              </Select>
            )}
          </Col>
          <Col span={8} className="card-header-action-buttons">
            <Space>
              {duplicateBtn}
              <Button
                icon={<EyeOutlined />} 
                type="primary" 
                onClick={(e) => showExperienceHelper(experience, resourceName, "default", currentUser, history, dispatch, e, location, setLogs)} 
                className="experience-item-preview-card-open-button"
              >
                {mrIntl("CommonText.open")}
              </Button>
              <Tooltip
                title={mrIntl("ExperienceItemPreview.close_preview")}
                placement="top"
              >
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => closePreview()}
                  shape="circle"
                />
              </Tooltip>
            </Space>
          </Col>
        </Row>
      }
    >
      <Col span={24}>
        {experienceLoading ? (
          <Skeleton paragraph={{ rows: 20 }} />
        ) : (
          <Suspense fallback={<Skeleton active />}>
            <div className="experience-item">
              {/* Render the active topic title */}
              {activeTopic && (
                <>
                  <div className="segment-list-item unstyled-list-item topic-title">
                    <h3>
                      <b>
                        <RenderHtml
                          text={activeTopic.section_segment_attributes.title}
                        />
                      </b>
                    </h3>
                  </div>

                  {/* Render the active topic's segments */}
                  <div className="segments-list">
                    <Route
                      path={`${path}`}
                      render={(routeProps) => (
                        <LazySegments
                          doNotReloadOnUpdate={true}
                          experienceViewMode="apEditTest"
                          showExperiencePreview={true}
                          experienceProps={{ item: experience }}
                          embedded={false}
                          mobileUploadMode={false}
                          rearrangeMode={false}
                          readingModeEnabled={false}
                          printMode={false}
                          topicProps={null}
                          parentId={activeTopic.id}
                          parentIdxs={[
                            topics.findIndex(
                              (topic) => topic.id === activeTopic.id
                            ),
                          ]}
                          nestingLevel={1}
                          gradingConfig={{}}
                          params={{
                            by_experience_id: experience.id,
                            by_parent_id: activeTopic.id,
                          }}
                          createUpdateParams={{
                            experience_id: experience.id,
                            parent_id: activeTopic.id,
                          }}
                          libraryModalProps={{}}
                          isRoute={false}
                          {...routeProps}
                          config={{
                            list: {
                              shouldExecuteRequest: false,
                            },
                          }}
                        />
                      )}
                    />
                  </div>
                </>
              )}
            </div>
          </Suspense>
        )}
      </Col>
    </Card>
  );
};

export default ExperienceItemPreview;
